import { Camera } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useOptionalClinicalCamera } from '@/components/camera/ClinicalCameraContext';
import { PATIENT_FILE_RE } from './SidebarDictateButton';

interface Props {
  /** Fecha o drawer mobile depois do clique. */
  onCloseMobile?: () => void;
  /** Versão compacta (só ícone) na sidebar desktop colapsada. */
  isDesktopCollapsed?: boolean;
}

/**
 * Atalho "Câmera" na sidebar, logo abaixo do "Gravar áudio". Mesmas regras
 * dos vizinhos: só fica ativo dentro da ficha de um paciente; fora dela,
 * aparece desabilitado.
 *
 * Tira foto pro paciente aberto: no celular/tablet abre a câmera do aparelho;
 * no computador abre a webcam (ou, sem webcam, o "Anexar imagens"). A foto
 * vai pro prontuário pelo modal de sempre — ver `ClinicalCameraContext`.
 */
export const SidebarCameraButton = ({ onCloseMobile, isDesktopCollapsed = false }: Props) => {
  const ctx = useOptionalClinicalCamera();
  const { pathname } = useLocation();

  if (!ctx) return null;

  const onPatientFile = PATIENT_FILE_RE.test(pathname);
  const enabled = onPatientFile && ctx.available;

  const base = `mb-2 rounded-lg flex items-center gap-3 border transition-colors ${
    isDesktopCollapsed
      ? 'md:mx-2 md:w-[calc(100%-1rem)] md:justify-center md:px-0 md:py-2 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
      : 'mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
  }`;

  if (!enabled) {
    return (
      <button
        type="button"
        disabled
        title={
          !onPatientFile
            ? 'Abra a ficha de um paciente para tirar foto.'
            : 'Este login não tem permissão para anexar imagens à ficha.'
        }
        className={`${base} text-on-surface-variant/50 border-dashed border-outline-variant/20 cursor-not-allowed`}
      >
        <Camera size={16} className="shrink-0" />
        <span className={`text-sm flex-1 text-left truncate ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
          Câmera
        </span>
        {!onPatientFile && (
          <span
            className={`text-[10px] uppercase tracking-wider text-on-surface-variant/40 shrink-0 ${isDesktopCollapsed ? 'md:hidden' : ''}`}
          >
            Abra paciente
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        ctx.shoot();
        onCloseMobile?.();
      }}
      title="Tirar foto e anexar ao prontuário deste paciente"
      className={`${base} bg-info/10 hover:bg-info/15 text-info border-info/30 font-medium`}
    >
      <Camera size={16} className="shrink-0" />
      <span className={`text-sm flex-1 text-left ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
        Câmera
      </span>
    </button>
  );
};
